/** @format */

import { ActivatedRoute, Router } from "@angular/router";
import { Component, ViewChild, ElementRef } from "@angular/core";

import { AuthService } from "src/app/services/auth/auth.service";
import { CompanyService } from "src/app/services/company/company.service";
import { ErrorHandlerService } from "src/app/services/errorHandler/error-handler.service";
import { DialogService } from "src/app/services/modal/dialog.service";
import { Plant } from "src/app/models/plant.model";
import { PlantService } from "src/app/services/plant/plant.service";
import { ToastService } from "src/app/services/toast/toast.service";

import constants from "src/app/constants";
import { Roles } from "src/app/auth/roles";

interface BulkUser {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  isValid: boolean;
  errors: string[];
}

@Component({
  standalone: false,
  selector: "app-add-admin-user",
  templateUrl: "./add-admin-user.component.html",
  styleUrls: ["./add-admin-user.component.css"],
})
export class AddAdminUserComponent {
	readonly Roles = Roles;
  @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;
  selectedValues = {
    company: {
      companyId: 0,
      companyName: "",
      isDeleted: false,
    },
    plant: {
      plantId: 0,
      plantName: "",
      isDeleted: false,
    },
  };

  constructor(
    private plantService: PlantService,
    private route: ActivatedRoute,
    private router: Router,
    public dialogService: DialogService,
    private userService: CompanyService,
    public toastService: ToastService,
    private authService: AuthService,
    private errorService: ErrorHandlerService,
  ) {}

	addAdminRequest = {
		firstName: "",
		lastName: "",
		username: "",
		contact: "",
		Roles: [] as string[],
		ids: [] as number[],
	}

  errorMessage: any = {
    success: true,
    message: "",
  };

  activePlants: Plant[] = [];
  selectedPlants: any = [];
  regex = "[a-zA-Z0-9._%+'-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}";
  nameRegex = /^[a-zA-Z0-9'\-\s]*$/;
  activeStep = 1;
  currentRole: any;
  companyId: any;

  addAdmin = false;

  firstRender = false;
  searchText: String = "";

  showFirstNameError = false;
  showLastNameError = false;
  firstNameError = "";
  lastNameError = "";

	onFirstNameInput() {
		if (this.addAdminRequest.firstName.length > 0 && !this.nameRegex.test(this.addAdminRequest.firstName)) {
			this.firstNameError = "Only single quote ( ' ) and hyphen ( - ) are allowed as special characters";
			this.showFirstNameError = true;
		} else {
			this.showFirstNameError = false;
		}
	}

	onLastNameInput() {
		if (this.addAdminRequest.lastName.length > 0 && !this.nameRegex.test(this.addAdminRequest.lastName)) {
			this.lastNameError = "Only single quote ( ' ) and hyphen ( - ) are allowed as special characters";
			this.showLastNameError = true;
		} else {
			this.showLastNameError = false;
		}
	}

	setActiveStep(step: number) {
		if (step == 2 && this.addAdminRequest.contact.length > 0 && this.addAdminRequest.contact.length < 16 && !this.isBulkMode) {
			this.errorService.handleError({error: {message: "Contact number must be 10 digits long."}} as any, 'Invalid contact number')
			return
		}
		this.activeStep = step
	}

  isSelectedPlant(plant: Plant) {
    var index = this.selectedPlants.findIndex((x: Plant) => x == plant);
    return index >= 0;
  }

  showSaveModal() {
    this.addAdmin = true;
  }

  closeModal() {
    this.addAdmin = false;
  }

  addPlant(data: Plant) {
    this.selectedPlants.push(data);
    this.addAdminRequest.ids.push(data.plantId);
  }

  removePlant(index: number) {
    this.selectedPlants.splice(index, 1);
    this.addAdminRequest.ids.splice(index, 1);
  }

  transferData(success: boolean = false) {
    this.firstRender = true;
    this.router.navigateByUrl("settings/user", {
      state: {
        selectedValues: { ...this.selectedValues },
        firstRender: this.firstRender,
        prevPage: success ? constants.addAdminUser : null,
        success: success,
      },
    });
  }

  ngOnInit(): void {
    this.currentRole = localStorage.getItem("role");
    this.selectedValues = history.state;
    let companyIdToUse: any;

    if (this.currentRole === Roles.admin) {
      this.companyId = localStorage.getItem("companyId");
      companyIdToUse = this.companyId;
    }

    if (this.currentRole === Roles.superAdmin) {
      companyIdToUse = this.selectedValues.company.companyId.toString();
    }

		this.plantService.retrieveCompanyPlants(companyIdToUse).subscribe({
			next: (plants) => {
				this.activePlants = plants				
			},
			error: (error) => {
				this.errorMessage = this.errorService.handleRetrievalErrors(error)
			},
		})
	}


  isValidStepOne() {
    if (
      this.addAdminRequest.firstName.length >= 2 &&
      this.addAdminRequest.lastName.length >= 2 &&
      this.addAdminRequest.username.match(this.regex) &&
      this.nameRegex.test(this.addAdminRequest.firstName) &&
      this.nameRegex.test(this.addAdminRequest.lastName)
    ) {
      return true;
    }
    return false;
  }

  enableNextBtn() {
    if (this.isBulkMode) return false;
    if (
      (this.activeStep === 1 && !this.isValidStepOne()) ||
      (this.activeStep === 2 && this.selectedPlants.length == 0)
    ) {
      return false;
    }
    return true;
  }

	formatContact(event: any) {
		let value = event.target.value.replace(/\D/g, '')
		let formatted = ''
		
		if (value.length === 0) {
			this.addAdminRequest.contact = ''
			return
		}
		
		if (value[0] !== '1') {
			value = '1' + value
		}
		
		formatted = '1'
		if (value.length > 1) {
			formatted += ' (' + value.substring(1, 4)
		}
		if (value.length >= 4) {
			formatted += ')'
		}
		if (value.length > 4) {
			formatted += ' ' + value.substring(4, 7)
		}
		if (value.length > 7) {
			formatted += ' ' + value.substring(7, 11)
		}
		
		this.addAdminRequest.contact = formatted
	}

	addAdminUser() {
		// Add variables to request object
		this.addAdminRequest.Roles.push("Admin")
		this.addAdminRequest.contact = this.addAdminRequest.contact.replace(/\D/g, '')

		this.authService.createKeycloakUser(this.addAdminRequest).subscribe({
			next: (response) => {
				this.transferData(true)
				this.toastService.showToast = true
				this.toastService.toastText = "User successfully added!"
				setTimeout(() => {
					this.toastService.showToast = false
				}, 5000)
			},
			error: (error) => {
				this.errorService.handleError(error, 'Error creating admin user')
			},
		})
	}

  // Bulk upload properties
  uploadedFileName: string = "";
  uploadProgress: number = 0;
  uploadStatus: "idle" | "uploading" | "complete" | "error" = "idle";
  showUploadStatus: boolean = false;
  bulkUsers: BulkUser[] = [];
  csvValidated: boolean = false;
  validationMessage: string = "";
  showBulkUploadResults: boolean = false;
  bulkUploadResults: any = null;
  isBulkMode: boolean = false;

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.uploadedFileName = file.name;
    this.showUploadStatus = true;
    this.uploadStatus = "uploading";
    this.uploadProgress = 0;
    this.csvValidated = false;
    this.validationMessage = "";
    this.bulkUsers = [];
    this.isBulkMode = true;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      this.simulateUploadProgress(() => {
        this.parseCsvContent(content);
      });
    };
    reader.onerror = () => {
      this.uploadStatus = "error";
      this.validationMessage = "Error reading file. Please try again.";
    };
    reader.readAsText(file);

    input.value = "";
  }

  simulateUploadProgress(callback: () => void): void {
    const interval = setInterval(() => {
      if (this.uploadProgress < 90) {
        this.uploadProgress += 10;
      } else {
        clearInterval(interval);
        this.uploadProgress = 100;
        this.uploadStatus = "complete";
        callback();
      }
    }, 100);
  }

  parseCsvContent(content: string): void {
    const lines = content.split("\n").filter((line) => line.trim() !== "");

    if (lines.length < 2) {
      this.csvValidated = false;
      this.validationMessage =
        "CSV file is empty or has no data rows. Please check the file and try again.";
      return;
    }

    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

    const hasRequiredHeaders =
      headers.includes("first name") &&
      headers.includes("last name") &&
      headers.includes("email address");

    if (!hasRequiredHeaders) {
      this.csvValidated = false;
      this.validationMessage =
        "Invalid CSV format. Required headers: First Name, Last Name, Email Address. Please download the sample CSV file.";
      return;
    }

    const firstNameIdx = headers.indexOf("first name");
    const lastNameIdx = headers.indexOf("last name");
    const emailIdx = headers.indexOf("email address");
    const phoneIdx = headers.indexOf("contact number");

    const users: BulkUser[] = [];
    let hasErrors = false;

    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCsvLine(lines[i]);
      if (values.length < 3) continue;

      const user: BulkUser = {
        firstName: values[firstNameIdx]?.trim() || "",
        lastName: values[lastNameIdx]?.trim() || "",
        email: values[emailIdx]?.trim() || "",
        phoneNumber: phoneIdx >= 0 ? values[phoneIdx]?.trim() : undefined,
        isValid: true,
        errors: [],
      };

      this.validateBulkUser(user);
      if (!user.isValid) hasErrors = true;
      users.push(user);
    }

    this.bulkUsers = users;

    if (users.length === 0) {
      this.csvValidated = false;
      this.validationMessage = "No valid data rows found in the CSV file.";
    } else if (hasErrors) {
      this.csvValidated = false;
      const invalidUsers = users.filter((u) => !u.isValid);
      const errorDetails = invalidUsers
        .map(
          (u) => `Row "${u.firstName} ${u.lastName}": ${u.errors.join(", ")}`,
        )
        .join(" | ");
      this.validationMessage = `${invalidUsers.length} of ${users.length} rows have validation errors: ${errorDetails}`;
    } else {
      this.csvValidated = true;
      this.validationMessage = `${users.length} users ready to be uploaded.`;
    }
  }

  parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current);
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  }

  validateBulkUser(user: BulkUser): void {
    const nameRegex = /^[a-zA-Z0-9'\-\s]*$/;
    const emailRegex = /^[a-zA-Z0-9._%+'-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    user.errors = [];
    user.isValid = true;

    if (
      !user.firstName ||
      user.firstName.length < 2 ||
      user.firstName.length > 35
    ) {
      user.errors.push("First name must be 2-35 characters");
      user.isValid = false;
    } else if (!nameRegex.test(user.firstName)) {
      user.errors.push("First name contains invalid characters");
      user.isValid = false;
    }

    if (
      !user.lastName ||
      user.lastName.length < 2 ||
      user.lastName.length > 35
    ) {
      user.errors.push("Last name must be 2-35 characters");
      user.isValid = false;
    } else if (!nameRegex.test(user.lastName)) {
      user.errors.push("Last name contains invalid characters");
      user.isValid = false;
    }

    if (!user.email || !emailRegex.test(user.email)) {
      user.errors.push("Invalid email address");
      user.isValid = false;
    }

    if (user.phoneNumber && user.phoneNumber.length > 0) {
      const digitsOnly = user.phoneNumber.replace(/['\-\s()]/g, '');
      const phoneRegex = /^\d{11}$/;
      if (!phoneRegex.test(digitsOnly)) {
        user.errors.push("Phone number must be exactly 11 digits");
        user.isValid = false;
      }
    }
  }

  downloadSampleCsv(): void {
    const csvContent =
      "First Name,Last Name,Email Address,Contact Number\nJohn,Doe,john.doe@example.com,12345678901\nJane,Smith,jane.smith@example.com,";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_admin_users_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  }

  enableBulkNextBtn(): boolean {
    return this.csvValidated && this.bulkUsers.length > 0;
  }

  submitBulkAdminUsers(): void {
    if (this.selectedPlants.length === 0) return;

    const plantIds = this.selectedPlants.map((p: Plant) => p.plantId);

    const payload = {
      Users: this.bulkUsers.map((u) => ({
        FirstName: u.firstName,
        LastName: u.lastName,
        Email: u.email,
        PhoneNumber: u.phoneNumber || null,
      })),
      Roles: ["Admin"],
      ids: plantIds,
    };

    this.authService.createKeycloakUsersBulk(payload).subscribe({
      next: (response) => {
        this.bulkUploadResults = response.data;
        this.showBulkUploadResults = true;

        if (response.data.failedCount === 0) {
          this.toastService.showToast = true;
          this.toastService.toastText = `Successfully registered ${response.data.successCount} admin users`;
          this.toastService.isError = false;

          setTimeout(() => {
            this.toastService.showToast = false;
            this.transferData();
          }, 6000);
        }
      },
      error: (error) => {
        this.errorService.handleError(error, "Error during bulk registration");
      },
    });
  }

  resetBulkUpload(): void {
    this.uploadedFileName = "";
    this.uploadProgress = 0;
    this.uploadStatus = "idle";
    this.showUploadStatus = false;
    this.bulkUsers = [];
    this.csvValidated = false;
    this.validationMessage = "";
    this.showBulkUploadResults = false;
    this.bulkUploadResults = null;
    this.isBulkMode = false;
  }
}
